import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Radio, Skeleton } from '@mui/material';
import moment from 'moment/moment';
import {
    AddressTypeSelect,
    ApptAddressOption,
    apptAddressGroupBy,
    renderApptAddressGroup,
    CreateChancel,
    CustomizedSwitch,
    DateTypeSelect,
    MinLoader,
    SelectAutocompletePagination, SelectAutocompletePaginationMultiple,
    SelectTypeAutocomplete,
    TimeInput,
    ToolInfo,
} from 'components';
import {
    clientStatusEnums,
    enumValues,
    ErrorText,
    FindErrorItem,
    FindLoad,
    Images,
    PermissionList,
    renderClientName, renderStaffName,
    renderLabeledAddress,
    reqTypeEnums,
    RolePermission, staffStatusEnums,
    useModal,
    User,
} from 'utils';
import { ModalHeader } from './common';
import { scheduleStatuses, SERVICE } from '../../constants';
import {
    changeDateTime,
    checkApptRequires,
    checkApptRequiresFields,
    checkFrequencyParams,
    checkFrequencyValid,
    // clientAddresses,
    apptAddressesList,
    findApptAddressByValue,
    findApptAddressItem,
    defaultInitialInputs,
    mergePayCodes,
    renderServiceRow,
    renderTimes,
    renderUnitSize,
    ServiceActionLoads,
    serviceTypeEnum,
    chargeRateColor,
    flattenChargeRateGroups,
} from './constants';
import { SelectStaff } from './selectStaff';
import {
    adminActions,
    appointmentActions,
    clientActions,
    httpRequestsOnErrorsActions,
    supervisionActions,
    systemActions,
} from 'store';
import { CreateMultiple } from './createMultiple';
import { EditMultiple } from './editMultiple';
import { ConfMultiple } from './confMultiple';
import { UnassignedConfirm } from './unassignedConfirm';
import { TemplateApptActions } from './common/templateApptActions';
import { ApptErrors } from './common/apptErrors';
import { ApptDiagnosis } from './common/apptDiagnosis';
import { appointmentService } from '../../../../store/appointment/appointment.service';
import { checkCreatePermission } from '../constants';
import { frequencyEnumsConvert } from '../../../client/clientModals/constants';

const GET_CLIENT_AUTHORIZATIONS = 'GET_CLIENT_AUTHORIZATIONS_APPT';
const GET_PLACES = 'GET_PLACES';
const GET_APPOINTMENT_DATES = 'GET_APPOINTMENT_DATES_BY_ID';
const GET_APPT_ADDRESSES = 'GET_APPT_ADDRESSES';
const GET_ELIGIBLE_CHARGE_RATES = 'GET_ELIGIBLE_CHARGE_RATES';
/**
 * Under End Time: this appointment's units, then — when the authorized service has a weekly/monthly
 * limit — what is left in that week/month **if this appointment is booked**, with a bar of how full
 * it gets. `usage.used` never counts the appointment being edited, so subtracting `units` is right
 * for create and edit alike. Amber at 80%+, red ("Over by N") when it does not fit.
 */
const ApptUnitsSummary = ({ units, usage }) => {
    const hasLimit = usage && usage.limit > 0;
    const remainingAfter = hasLimit ? usage.remaining - units : 0;
    const fits = !hasLimit || remainingAfter >= 0;
    const filled = hasLimit ? Math.min(((usage.used + units) / usage.limit) * 100, 100) : 0;
    const level = !fits ? 'is-over' : filled >= 80 ? 'is-near' : '';
    const period = usage?.mode === frequencyEnumsConvert.WEEKLY ? 'week' : 'month';
    const format = (value) => Number(Number(value).toFixed(2));

    return (
        <div className={`appt-units-summary ${level}`}>
            <div className="appt-units-summary__row">
                <span className="appt-units-summary__label">Units</span>
                <span className="appt-units-summary__value">{units.toFixed(2)}</span>
            </div>
            {hasLimit && (
                <>
                    <div className="appt-units-summary__row">
                        <span className="appt-units-summary__label">Remaining this {period}</span>
                        <span className="appt-units-summary__value">
                            {fits ? `${format(remainingAfter)} of ${format(usage.limit)}` : `Over by ${format(-remainingAfter)}`}
                        </span>
                    </div>
                    <div
                        className="appt-units-summary__bar"
                        role="progressbar"
                        aria-label={`Units used this ${period}, including this appointment`}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={Math.round(filled)}
                    >
                        <span style={{ width: `${filled}%` }} />
                    </div>
                </>
            )}
        </div>
    );
};

export const ServiceAppt = ({ modalDate, date, handleOpenClose, duplicate }) => {
    const REQUEST_TYPE = modalDate ? 'EDIT_APPOINTMENT' : 'CREATE_APPOINTMENT';
    const {
        places,
        allPaycodes,
        clientStaffs,
        appointmentDates,
        // clientItemInfo,
        // clientContacts,
        clientsAuthorizationsAppt,
        supervisionSummary,
        apptAddresses,
        eligibleChargeRates,
    } = useSelector((state) => ({
        places: state.system.places,
        allPaycodes: state.admins.allPaycodes,
        clientStaffs: state.appointment.clientStaffs,
        appointmentDates: state.appointment.appointmentDates,
        // clientItemInfo: state.client.clientItemInfo,
        // clientContacts: state.client.clientContacts,
        clientsAuthorizationsAppt: state.client.clientsAuthorizationsAppt,
        supervisionSummary: state.supervision.supervisionSummary,
        apptAddresses: state.appointment.apptAddresses,
        eligibleChargeRates: state.appointment.eligibleChargeRates,
    }));
    const dispatch = useDispatch();
    const backError = FindErrorItem(REQUEST_TYPE);
    const loader = FindLoad(REQUEST_TYPE);
    const { open, close } = useModal();
    const resetParams = modalDate ? modalDate : duplicate ? duplicate : null;
    const location = useLocation();
    const scheduleClients = location?.pathname === '/schedule' ? location?.state?.clientFull : null;
    const filteredClient = !resetParams && scheduleClients?.length === 1 ? scheduleClients[0] : null;
    const [createMultiple, setCreateMultiple] = useState(false);
    const [inputs, setInputs] = useState(false);
    const [error, setError] = useState('');
    const [errorList, setErrorList] = useState([]);
    const [currentStaff, setCurrentStaff] = useState(null);
    const [chargeRate, setChargeRate] = useState(null);
    const pendingPayCodeType = useRef(null);
    const [state, setState] = React.useState([]);
    const [occurrence, setOccurrence] = useState(0);
    const [frequencyLimit, setFrequencyLimit] = useState(null);
    const [checkFrequencyLoad, setCheckFrequencyLoad] = useState(null);
    const loadDates = FindLoad(GET_APPOINTMENT_DATES);
    const loadAuthorizations = FindLoad(GET_CLIENT_AUTHORIZATIONS);
    // The appointment payload's service has no populated serviceTypeId, so the selected row shows " - 97153"
    // until the client's authorizations load. Hold a skeleton for at least 1s, and longer if they're still loading.
    const [serviceRowDelay, setServiceRowDelay] = useState(!!resetParams?.authorizedService);
    const currentAuthorization = clientsAuthorizationsAppt?.find((i) => i?.id === inputs?.authorizedService);
    const showServiceRowSkeleton = serviceRowDelay || (!!loadAuthorizations?.length && !currentAuthorization);
    const defaultAuthorization = resetParams?.authorizedService && {
        payerName: resetParams?.authorizedService?.payer?.name,
        authorizationId: resetParams?.authorizedService?.authorizationId,
        ...resetParams?.authorizedService,
    };
    const apptUnits = renderUnitSize(inputs, currentAuthorization || defaultAuthorization);
    const frequencyUsage = frequencyLimit?.frequencyUsage;
    const authorization = resetParams?.id
        ? inputs?.authorizedService && (currentAuthorization || defaultAuthorization)
        : currentAuthorization;
    const apptBetweenDates = {
        startDate: moment.utc(inputs?.betweenStartDate ? inputs?.betweenStartDate : appointmentDates?.startDate).format('YYYY-MM-DD'),
        endDate: moment.utc(inputs?.betweenEndDate ? inputs.betweenEndDate : appointmentDates?.endDate).format('YYYY-MM-DD'),
    };
    const hasWarningPermission = RolePermission([PermissionList.APPT_CREATE_WARNING?.code]);
    const reqType = checkCreatePermission(SERVICE);
    const { userInfo } = User();
    const hasChargeRatePermission = RolePermission([PermissionList.APPT_ELIGIBLE_CHARGE_RATE_READ?.code]);
    const selectedStaffId = inputs?.staff?.id || inputs?.staff || currentStaff?.id;
    // With APPT_UNASSIGNED_MANAGE (5.53) a scheduler (not self-only) may leave Staff and Staff Paycode empty:
    // the appt is saved as an "Unassigned" slot and staff is assigned later. Without it the form is unchanged.
    const canScheduleUnassigned =
        RolePermission([PermissionList.APPT_UNASSIGNED_MANAGE?.code]) &&
        (reqType === reqTypeEnums.ALL || RolePermission([PermissionList.APPT_CONNECTED_CREATE?.code]));
    const isUnassigned = canScheduleUnassigned && !selectedStaffId;
    const showChargeRate =
        hasChargeRatePermission && !!inputs?.client && !!inputs?.authorizedService && (!!selectedStaffId || canScheduleUnassigned);
    const chargeRatesLoad = FindLoad(GET_ELIGIBLE_CHARGE_RATES);
    const payCodesLoad = FindLoad(ServiceActionLoads.GET_ALL_PAY_CODES);
    const chargeRateList = flattenChargeRateGroups(eligibleChargeRates);

    // An unassigned appt loads the rates even without the charge-rate permission: its rate can't come from a
    // staff, so it takes the API's default (highest billed rate) picked below.
    const loadChargeRates = showChargeRate || (isUnassigned && !!inputs?.client && !!inputs?.authorizedService);

    useEffect(() => {
        if (loadChargeRates) {
            dispatch(appointmentActions.getEligibleChargeRates(selectedStaffId, inputs.authorizedService, inputs.client));
        } else {
            dispatch(appointmentActions.removeEligibleChargeRates());
        }
    }, [inputs?.client, inputs?.authorizedService, selectedStaffId]);

    // Keep the current pick if it survived the refetch; otherwise fall back to the charge rate the
    // appointment was saved with (edit/duplicate), then to the API's default (highest billed rate).
    useEffect(() => {
        const flat = flattenChargeRateGroups(eligibleChargeRates);
        if (!flat.length) {
            pendingPayCodeType.current = null;
            if (chargeRate) setChargeRate(null);
            return;
        }
        if (flat.some((i) => i?.chargeRateId === chargeRate?.chargeRateId)) return;

        const saved = resetParams?.chargeRateId || currentStaff?.chargeRateId;
        selectChargeRate(flat.find((i) => i?.chargeRateId === saved) || flat.find((i) => i?.isDefault) || flat[0], true);
    }, [eligibleChargeRates]);

    useEffect(() => {
        const mergedStaffs = clientStaffs?.length
            ? clientStaffs.flatMap((item) =>
                item.staffs.map((staff) => ({
                    ...staff,
                    chargeRateId: item?.chargeRateId,
                    payCodeType: item?.payCodeTypeId,
                    payCodeTypeCode: item?.payCodeTypeCode,
                    payCodeTypeName: item?.payCodeTypeName,
                })),
            )
            : [];
        if (reqType === reqTypeEnums.SELF && mergedStaffs?.length) {
            const current = mergedStaffs?.find((i) => i?.id === userInfo?.id);
            if (current) {
                selectStaff(current);
            }
        }
        if (clientStaffs && mergedStaffs?.length === 1) {
            selectStaff(mergedStaffs[0]);
        }
    }, [clientStaffs]);

    useEffect(() => {
        dispatch(appointmentActions.removeAppointmentDates());
        dispatch(httpRequestsOnErrorsActions.removeError(REQUEST_TYPE));
        // Clear any addresses / charge rates loaded for a previous appt so they don't leak into this one.
        return () => {
            dispatch(appointmentActions.removeApptAddresses());
            dispatch(appointmentActions.removeEligibleChargeRates());
        };
    }, []);

    // Location options come from the client + client contacts + assigned staff + office. Fetched once a
    // client and a staff are chosen — or with the client alone for an unassigned appt (no staff).
    // `authorizedService` is a dependency because changing the service clears the list (`removeStaffs`):
    // with a staff, picking the staff again refetches it, but an unassigned appt has no staff to pick.
    useEffect(() => {
        const clientId = inputs?.client;
        if (clientId && (selectedStaffId || isUnassigned)) {
            dispatch(appointmentActions.getApptAddresses(clientId, selectedStaffId));
        }
    }, [inputs?.client, inputs?.authorizedService, selectedStaffId, isUnassigned]);

    useEffect(() => {
        if (appointmentDates && (!inputs?.betweenStartDate || !inputs?.betweenEndDate)) {
            setInputs({
                ...inputs,
                betweenStartDate: appointmentDates?.startDate,
                betweenEndDate: appointmentDates?.endDate,
            });
        }
    }, [appointmentDates]);

    useEffect(() => {
        if (apptAddresses && !modalDate?.id && !inputs?.location) {
            const list = apptAddressesList(apptAddresses);
            const preselect = findApptAddressItem(list, apptAddresses.default);
            if (preselect) {
                setInputs({
                    ...inputs,
                    location: preselect.id,
                    locationLabel: preselect.label,
                });
            }
        }
    }, [apptAddresses]);



    useEffect(() => {
        if (!serviceRowDelay) return;
        const timer = setTimeout(() => setServiceRowDelay(false), 1000);
        return () => clearTimeout(timer);
    }, [serviceRowDelay]);

    useEffect(() => {
        if (resetParams) {
            const setInfo = {
                ...resetParams,
                client: resetParams.client?.id,
                staff: resetParams.staff?.id,
                placeService: resetParams.posId,
                payCodeType: resetParams?.payCodeTypeId,
                authorizedService: resetParams.authorizedService?._id,
                requireProviderSignature: resetParams?.requireProviderSignature,
                requireSignature: resetParams?.requireSignature,
                requireNote: resetParams?.requireNote,
                startTime: resetParams.startTime?.slice(11, 16),
                endTime: resetParams.endTime?.slice(11, 16),
                supervision: resetParams?.supervision || false,
                supervisee: resetParams?.supervisee || null,
            };

            getStaffs(resetParams.client?.id, resetParams.authorizedService?._id);
            getClientMoreInfo(resetParams.client?.id);
            if (resetParams?.staff?.id) getStaffPayCodes(resetParams.staff.id);
            setInputs(setInfo);
            setCurrentStaff(null);
            // The frequency-check effect fires once setInputs(setInfo) commits.
        }
        if (!resetParams && date) {
            setInputs({
                startTime: date?.startTime?.slice(11, 16),
                endTime: date?.endTime?.slice(11, 16),
                startDate: date?.startDate,
                ...(filteredClient && { client: filteredClient.id }),
            });
            setCurrentStaff(null);
        }
        if (!resetParams && !date) {
            setInputs(filteredClient ? { client: filteredClient.id } : null);
            setCurrentStaff(null);
        }
        // Same follow-up a manual pick triggers in handleChange: the authorization list and
        // client details the rest of the form is built from.
        if (!resetParams && filteredClient) {
            getClientMoreInfo(filteredClient.id);
        }
    }, [resetParams, date]);

    useEffect(() => {
        if (resetParams && clientStaffs && !currentStaff && inputs?.staff) {
            clientStaffs?.forEach((item) =>
                item?.staffs?.find((i) => {
                    if (i?.id === resetParams?.staff?.id) {
                        setCurrentStaff({ chargeRateId: item?.chargeRateId, ...i });
                    }
                }),
            );
        }
    }, [resetParams, clientStaffs]);

    useEffect(() => {
        if (inputs?.client) {
            dispatch(supervisionActions.getSupervisionSummary({ clientId: inputs.client }));
        }
    }, [inputs?.client]);

    useEffect(() => {
        const payCodeList = mergePayCodes(allPaycodes);
        const pending = pendingPayCodeType.current;
        if (!pending || !payCodeList?.length) return;

        pendingPayCodeType.current = null;
        const current = payCodeList.find((i) => i?._id === pending?.payCodeTypeId);
        if (!current) return;
        setInputs((prevState) => ({
            ...prevState,
            payCodeType: current?._id,
            payCodeTypeName: current?.name || pending?.payCodeTypeName,
            payCodeTypeCode: pending?.payCodeTypeCode,
        }));
    }, [allPaycodes]);

    useEffect(() => {
        const payCodeList = mergePayCodes(allPaycodes);
        if (inputs?.defaultPayCodeType && !inputs?.payCodeType && payCodeList?.length) {
            const current = payCodeList?.find((i) => i?._id === inputs?.defaultPayCodeType);
            if (current) {
                // Merged, not written from the `inputs` snapshot: the pending-paycode effect above
                // runs in the same commit, and a snapshot write would drop what it just set.
                setInputs((prevState) => ({ ...prevState, payCodeType: current?._id }));
            }
        }
    }, [allPaycodes]);

    const getClientMoreInfo = (clientId) => {
        dispatch(clientActions.getClientsById(clientId));
        // dispatch(clientActions.getClientsContacts(clientId));
        dispatch(clientActions.getClientAuthorizationAppt(clientId, reqType));
    };

    const getStaffPayCodes = (staffId) => {
        dispatch(adminActions.getAllPayCodes(staffId));
    };

    const apptInfo = () => {
        const data = {
            type: SERVICE,
            client: inputs.client,
            authorizedService: inputs?.authorizedService,
            staff: inputs.staff?.id || inputs.staff || currentStaff?.id || null,
            payCodeType: inputs.payCodeType,
            placeService: inputs.placeService,
            startDate: inputs?.startDate && moment.utc(inputs.startDate).format('YYYY-MM-DD'),
            startTime: changeDateTime(inputs?.startDate, inputs?.startTime),
            endTime: changeDateTime(inputs?.startDate, inputs?.endTime),
            requireProviderSignature: inputs?.requireProviderSignature,
            requireSignature: inputs?.requireSignature,
            requireNote: inputs?.requireNote,
            editTemplate: createMultiple,
            // With the permission, the Charge Rate selector wins — it's the only place the user can
            // override the rate the staff suggestion came in with. Without it, old behavior: the
            // rate is whatever the selected staff carried.
            // An unassigned appt has no staff to carry a rate — it takes the loaded rate (the API's default when
            // the user can't pick one).
            chargeRate:
                ((hasChargeRatePermission || isUnassigned) && chargeRate?.chargeRateId) || currentStaff?.chargeRateId || null,
            telehealth: inputs?.telehealth,
            location: inputs?.location || null,
            locationLabel: (inputs?.location && inputs?.locationLabel) || null,
            supervision: inputs?.supervision || false,
            supervisee: inputs?.supervision ? inputs?.supervisee?.id || inputs?.supervisee || null : null,
        };

        inputs?.serviceType ? (data.serviceType = inputs?.serviceType) : delete data.serviceType;

        if (createMultiple) {
            data.endDate = moment.utc(inputs.endDate).format('YYYY-MM-DD');
            if ((modalDate && inputs?.betweenStartDate) || inputs?.betweenEndDate) {
                data.updateBetween = {
                    startDate: inputs?.betweenStartDate ? moment.utc(inputs?.betweenStartDate).format('YYYY-MM-DD') : null,
                    endDate: inputs?.betweenEndDate ? moment.utc(inputs?.betweenEndDate).format('YYYY-MM-DD') : null,
                };
            }
        } else {
            delete data.endDate;
        }
        return data;
    };

    const setMultipleAppts = () => {
        setCreateMultiple(!createMultiple);
        if (modalDate) {
            const params = {
                ...inputs,
            };
            delete params.endDate;
            delete params.supervisee;
            delete params.supervision;
            params.startDate = modalDate.startDate;
            setInputs(params);
        } else {
            const params = {
                ...inputs,
                ...defaultInitialInputs,
            };
            delete params.startDate;
            delete params.endDate;
            delete params.supervisee;
            delete params.supervision;
            setInputs(params);
        }
    };

    const removeStaffs = () => {
        dispatch(appointmentActions.removeStaffClients());
        dispatch(adminActions.clearAllPayCodes());
        dispatch(appointmentActions.removeApptAddresses());
        setCurrentStaff(null);
    };

    const getStaffs = (clientId, serviceId) => {
        dispatch(appointmentActions.getStaffClients(clientId, serviceId));
    };

    // Frequency/unit-limit check is driven off committed state, not fired imperatively from
    // handleChange. Running it in an effect guarantees it always reads the latest `inputs`
    // (and the authorization resolved from them), so it can never send stale params — e.g. the
    // old bug where clearing a service then selecting another sent unitCount = undefined because
    // the handler ran before setInputs and read a stale `currentAuthorization` from the closure.
    useEffect(() => {
        if (!checkFrequencyValid(inputs, modalDate, createMultiple, appointmentDates)) return;

        const auth = clientsAuthorizationsAppt?.find((i) => i?.id === inputs?.authorizedService) || currentAuthorization || defaultAuthorization;
        const apptUnits = renderUnitSize(inputs, auth, true);

        setCheckFrequencyLoad(true);
        const sendParams = checkFrequencyParams({
            params: inputs,
            createMultiple: createMultiple,
            modalDate: modalDate,
            appointmentDates: appointmentDates,
            apptUnits: apptUnits,
            apptCount: inputs?.occurrence || occurrence,
            selectedDays: state,
        });

        // Guard against out-of-order responses: if inputs change again before this resolves,
        // the cleanup marks it stale so a slower earlier request can't overwrite a newer result.
        let cancelled = false;
        appointmentService
            .checkFrequencyService(sendParams)
            .then((res) => {
                if (!cancelled) setFrequencyLimit(res?.data);
            })
            .catch(() => {})
            .finally(() => {
                if (!cancelled) setCheckFrequencyLoad(false);
            });

        return () => {
            cancelled = true;
        };
    }, [
        inputs?.startDate,
        inputs?.startTime,
        inputs?.endTime,
        inputs?.authorizedService,
        inputs?.endDate,
        inputs?.betweenStartDate,
        inputs?.betweenEndDate,
        inputs?.mode,
        inputs?.repeatConsecutive,
        inputs?.repeatCount,
        inputs?.repeatCountWeek,
        inputs?.repeatMonth,
        inputs?.repeatDayMonth,
        occurrence,
        createMultiple,
        appointmentDates,
        state,
        clientsAuthorizationsAppt,
    ]);

    const handleChange = (ev) => {
        const params = {
            ...inputs,
        };

        if (ev.target.name === 'client') {
            dispatch(clientActions.removeClientAuthorization());

            delete params.authorizedService;
            delete params.payCodeType;
            delete params.serviceType;
            delete params.location;
            delete params.staff;
            if (ev.target.value) {
                getClientMoreInfo(ev.target.value);
            }
            removeStaffs();
        }

        if (ev.target.name === 'authorizedService') {
            const currentAuthorization = clientsAuthorizationsAppt?.find((i) => i?.id === ev?.target?.value);

            if (ev.target.value) {
                getStaffs(inputs?.client, ev.target.value);
            }
            if (ev.target.value && currentAuthorization?.service?.type) {
                params.serviceType = currentAuthorization?.service?.type;
            }

            const checkServiceType = currentAuthorization?.service?.type
                ? currentAuthorization?.service?.type
                : inputs?.serviceType === serviceTypeEnum.DIRECT || !inputs?.serviceType;
            if (ev.target.value) {
                if (checkServiceType) {
                    // if(currentAuthorization?.service?.type === serviceTypeEnum.DIRECT) {
                    params.requireSignature = currentAuthorization?.service?.requireSignature;
                } else {
                    params.requireSignature = false;
                }
                params.requireProviderSignature = currentAuthorization?.service?.requireProviderSignature;
                params.requireNote = currentAuthorization?.service?.requireNotes;
            } else {
                delete params.requireProviderSignature;
                delete params.requireSignature;
                delete params.requireNote;
            }
            delete params.staff;
            delete params.payCodeType;
            delete params.location;
            removeStaffs();
        }

        if (ev.target.name === 'location') {
            // Addresses typed via "Add New Address" aren't in the list → no label.
            params.locationLabel = findApptAddressByValue(apptAddressesList(apptAddresses), ev.target.value)?.label || null;
        }

        if (ev.target.name === 'payCodeType') {
            delete params.payCodeTypeCode;
            delete params.payCodeTypeName;
        }

        if (ev?.target?.name === 'serviceType') {
            if (ev?.target?.value === serviceTypeEnum.DIRECT) {
                params.requireSignature = currentAuthorization?.service?.requireSignature;
            } else {
                params.requireSignature = false;
            }
        }

        ev.target.value ? (params[ev.target.name] = ev.target.value) : delete params[ev.target.name];
        backError?.error && dispatch(httpRequestsOnErrorsActions.removeError(REQUEST_TYPE));
        setInputs(params);
        (error === ev.target.name || error === ErrorText.dateError) && setError('');
        // error === ErrorText.dateError && setError('');
    };

    const selectStaff = (selected) => {
        const params = {
            ...inputs,
        };
        const prevStaffId = inputs?.staff?.id || inputs?.staff;
        if (prevStaffId && prevStaffId !== selected?.id) {
            delete params.location;
            dispatch(appointmentActions.removeApptAddresses());
        }
        params.staff = selected?.id;
        selected?.chargeRateId ? (params.chargeRateId = selected?.chargeRateId) : delete params.chargeRateId;

        if (selected?.payCodeType) {
            params.defaultPayCodeType = selected?.payCodeType;
        } else {
            delete params.defaultPayCodeType;
        }
        setInputs(params);
        setCurrentStaff(selected);
        if (selected?.id) {
            getStaffPayCodes(selected?.id);
        }
        if (error === 'staff') setError('');
    };

    const selectChargeRate = (selected, isAuto) => {
        setChargeRate(selected || null);
        pendingPayCodeType.current = null;

        setInputs((prevState) => {
            const params = { ...prevState };
            const payCodeTypeId = selected?.payCodeTypeId;
            if (!payCodeTypeId) return params;
            params.defaultPayCodeType = payCodeTypeId;
            if (isAuto && params?.payCodeType) return params;

            const current = !payCodesLoad?.length && mergePayCodes(allPaycodes)?.find((i) => i?._id === payCodeTypeId);
            if (!current) {
                pendingPayCodeType.current = selected;
                delete params.payCodeType;
                delete params.payCodeTypeName;
                delete params.payCodeTypeCode;
                return params;
            }

            params.payCodeType = current?._id;
            params.payCodeTypeName = current?.name || selected?.payCodeTypeName;
            params.payCodeTypeCode = selected?.payCodeTypeCode;
            return params;
        });
        if (error === 'payCodeType') setError('');
    };

    const changeRequired = (e, name) => {
        const checkValue = !inputs[name];
        setInputs((prevState) => ({ ...prevState, [name]: checkValue }));
    };

    const revalidateBeforeSubmit = async () => {
        if (hasWarningPermission) return true;
        if (!checkFrequencyValid(inputs, modalDate, createMultiple, appointmentDates)) return true;

        setCheckFrequencyLoad(true);
        try {
            const auth = currentAuthorization || defaultAuthorization;
            const units = renderUnitSize(inputs, auth, true);
            const sendParams = checkFrequencyParams({
                params: inputs,
                createMultiple,
                modalDate,
                appointmentDates,
                apptUnits: units,
                apptCount: inputs?.occurrence || occurrence,
                selectedDays: state,
            });
            const res = await appointmentService.checkFrequencyService(sendParams);
            const result = res?.data;
            setFrequencyLimit(result);
            if (result?.frequencyOverlimit || result?.availableUnitsExceed) {
                return false;
            }
        } catch (e) {
            // If check fails, allow submission (backend will validate)
        } finally {
            setCheckFrequencyLoad(false);
        }
        return true;
    };

    const handleCreate = async () => {
        const apptVerification = checkApptRequiresFields(inputs, createMultiple, undefined, canScheduleUnassigned);
        if (apptVerification !== 'valid') {
            setError(apptVerification);
            return;
        }
        if (!(await revalidateBeforeSubmit())) return;

        const date = apptInfo();
        const submit = () => {
            if (modalDate) {
                dispatch(appointmentActions.editAppointment(date, modalDate?.id));
            } else {
                dispatch(appointmentActions.createAppointment(date));
            }
        };
        if (isUnassigned) {
            open(
                <UnassignedConfirm
                    isEdit={!!modalDate?.id}
                    handleSubmit={() => {
                        close();
                        submit();
                    }}
                />,
            );
        } else {
            submit();
        }
    };

    async function createMultipleAppt() {
        const apptVerification = checkApptRequiresFields(inputs, createMultiple, state, canScheduleUnassigned);
        if (apptVerification !== 'valid') {
            setError(apptVerification);
            return;
        }
        if (!(await revalidateBeforeSubmit())) return;

        const week = {
            startDate: new Date(inputs.startDate),
            endDate: new Date(inputs.endDate),
            mode: inputs.mode,
            repeatCountWeek: +inputs.repeatCountWeek,
            repeatCheckWeek: [...state],
        };

        const mounthObject = {
            startDate: new Date(inputs.startDate),
            endDate: new Date(inputs.endDate),
            mode: inputs.mode,
            repeatDayMonth: +inputs.repeatDayMonth,
            repeatMonth: +inputs.repeatMonth,
        };

        !inputs.repeatDayMonth ? delete mounthObject['repeatDayMonth'] : '';
        !inputs.repeatMonth ? delete mounthObject['repeatMonth'] : '';
        const newObject = {
            startDate: new Date(inputs.startDate),
            endDate: new Date(inputs.endDate),
            mode: inputs.mode,
        };
        if (inputs.repeatConsecutive === 'repeatConsecutive') {
            newObject.repeatConsecutive = true;
        } else {
            newObject.repeatCount = +inputs.repeatCount;
        }

        const obj = inputs.mode === 'WEEKLY' ? week : inputs.mode === 'MONTHLY' ? mounthObject : newObject;

        const openConfirm = () => open(
            <ConfMultiple
                recurInfo={obj}
                handleSubmit={() =>
                    dispatch(
                        appointmentActions.appointmentFromTemplate({
                            pattern: { ...obj },
                            apptData: {
                                staffId: inputs?.staff,
                                ...apptInfo(),
                                template: {
                                    ...obj,
                                },
                            },
                        }),
                    )
                }
                loadType={ServiceActionLoads.APPOINTMENT_FROM_TEMPLATE}
                occurrence={occurrence}
                checkParams={{
                    staffId: inputs?.staff,
                    startTime: changeDateTime(obj?.startDate, inputs?.startTime),
                    endTime: changeDateTime(obj?.endDate, inputs?.endTime),
                }}
            />,
        );
        isUnassigned ? open(<UnassignedConfirm multiple handleSubmit={openConfirm} />) : openConfirm();
    }

    async function editMultipleAppt() {
        const apptEditVerification = checkApptRequiresFields(inputs, false, state, canScheduleUnassigned);
        if (apptEditVerification !== 'valid') {
            setError(apptEditVerification);
            return;
        }
        if (!(await revalidateBeforeSubmit())) return;

        const startTime = changeDateTime(modalDate?.startDate, inputs?.startTime);
        const endTime = changeDateTime(modalDate?.startDate, inputs?.endTime);

        const sendParams = {
            ...apptInfo(),
            startTime: startTime,
            endTime: endTime,
        };
        delete sendParams.startDate;
        delete sendParams.endDate;

        const openConfirm = () => open(
            <EditMultiple
                modalDate={{
                    templateId: modalDate?.template,
                    startTime: startTime,
                    endTime: endTime,
                    staffId: inputs?.staff,
                }}
                handleSubmit={() => dispatch(appointmentActions.editAppointment(sendParams, modalDate?.id))}
                loadType={REQUEST_TYPE}
                occurrence={occurrence}
                dateBetween={{
                    startDate: inputs?.betweenStartDate ? moment.utc(inputs?.betweenStartDate).format('YYYY-MM-DD') : null,
                    endDate: inputs?.betweenEndDate ? moment.utc(inputs?.betweenEndDate).format('YYYY-MM-DD') : null,
                }}
            />,
        );
        isUnassigned ? open(<UnassignedConfirm multiple isEdit handleSubmit={openConfirm} />) : openConfirm();
    }

    const changeOccurrence = (count) => {
        setOccurrence(count);
        // The frequency-check effect re-runs on the `occurrence` change.
    };

    return (
        <div>
            <ModalHeader
                modalDate={modalDate}
                type={SERVICE}
                createMultiple={createMultiple}
                setMultipleAppts={setMultipleAppts}
                apptDates={appointmentDates}
                setInputs={setInputs}
                inputs={inputs}
            />

            <div>
                <div className="service-inputs-wrapper">
                    <div className="full-width">
                        <p className="date-time-text">Client</p>
                        <SelectAutocompletePagination
                            style={'full-width'}
                            defaultStatuses={[
                                clientStatusEnums.ACTIVE,
                                clientStatusEnums.ON_HOLD,
                                clientStatusEnums.FBA,
                                clientStatusEnums.WAIT_LIST,
                                clientStatusEnums.INVITED,
                            ]}
                            title={'Client*'}
                            name={'client'}
                            handleSelect={handleChange}
                            defaultValue={inputs?.client}
                            renderValue={(i) => renderClientName(i)}
                            type={'client'}
                            error={error}
                            typeError={error === 'client' ? `Client ${ErrorText.isRequired}` : ''}
                            currentSelected={resetParams?.client || filteredClient}
                        />
                    </div>

                    <div className="service-inputs-box">
                        <SelectTypeAutocomplete
                            loadType={GET_PLACES}
                            style={'full-width'}
                            title={'Place of Service*'}
                            name={'placeService'}
                            handleSelect={handleChange}
                            defaultValue={inputs?.placeService}
                            defaultSelected={{ name: resetParams?.posName }}
                            list={places || []}
                            error={error}
                            typeError={error === 'placeService' ? `Place of service ${ErrorText.isRequired}` : ''}
                            renderValue={(i) => i?.name}
                            refresh={() => dispatch(systemActions.getPlaces())}
                        />
                        <SelectTypeAutocomplete
                            disabled={modalDate?.status === scheduleStatuses?.COMPLETED}
                            style={'full-width'}
                            title={'Type*'}
                            name={'serviceType'}
                            handleSelect={handleChange}
                            defaultValue={inputs?.serviceType}
                            list={enumValues.FUNDING_MODIFIER_SERVICE_TYPES}
                            error={error}
                            typeError={error === 'serviceType' ? `Type ${ErrorText.isRequired}` : ''}
                            renderValue={(i) => i?.title}
                        />
                    </div>
                </div>

                <div className="authorized-service-box full-width">
                    <div className="flex-end" style={{ marginBottom: 16 }}>
                        <div className="switcher-style">
                            <p>Show expired authorizations</p>
                            <CustomizedSwitch
                                checked={inputs?.showExpired}
                                handleClick={() => {
                                    setInputs((prev) => ({ ...prev, showExpired: !prev?.showExpired }));
                                }}
                            />
                        </div>
                    </div>
                    <SelectTypeAutocomplete
                        renderFieldValue={
                            inputs?.authorizedService && (
                                <p className="selected-authorization-info">
                                    {showServiceRowSkeleton ? (
                                        <Skeleton variant="rounded" width="100%" height={24} />
                                    ) : (
                                        renderServiceRow(currentAuthorization || defaultAuthorization, inputs, createMultiple)
                                    )}
                                </p>
                            )
                        }
                        loadType={GET_CLIENT_AUTHORIZATIONS}
                        disabled={!inputs?.client}
                        title={'Service*'}
                        name={'authorizedService'}
                        handleSelect={handleChange}
                        defaultValue={inputs?.authorizedService}
                        defaultSelected={{
                            payerName: resetParams?.authorizedService?.payer?.name,
                            payerShortName: resetParams?.authorizedService?.payer?.payerShortName,
                            authorizationId: resetParams?.authorizedService?.authorizationId,
                        }}
                        list={
                            (clientsAuthorizationsAppt || []).filter((i) => {
                                if (!i?.active) return false;
                                if (inputs?.showExpired) return true;
                                const today = moment().format('YYYY-MM-DD');
                                return i?.endDate >= today;
                            })
                        }
                        customOptions={(option) => renderServiceRow(option, inputs, createMultiple)}
                        // renderValue={(i) => i?.payerName && `${i?.payerShortName ? i?.payerShortName : i?.payerName} - ${i?.authorizationId}`}
                        renderValue={(i) => `  `}
                        error={error}
                        typeError={error === 'authorizedService' ? `Service ${ErrorText.isRequired}` : ''}
                        refresh={() => {
                            dispatch(clientActions.getClientAuthorizationAppt(inputs?.client));
                        }}
                    />
                    <ApptDiagnosis
                        clientId={inputs?.client}
                        authorizedServiceId={inputs?.authorizedService}
                        date={inputs?.startDate && moment.utc(inputs.startDate).format('YYYY-MM-DD')}
                    />
                </div>

                {(() => {
                    const auth = currentAuthorization || defaultAuthorization;
                    if (auth?.billable === false) {
                        return (
                            <div className="authorization-marked-non-billable">
                                <img src={Images.warningInfo} alt="warning" style={{ width: 16, height: 16 }} />
                                <p>This authorization is marked as non-billable. The appointment will not be billed.</p>
                            </div>
                        );
                    }
                    return null;
                })()}

                {(() => {
                    const auth = currentAuthorization || defaultAuthorization;
                    const payerId = auth?.payerId;
                    const item = payerId && supervisionSummary?.items?.find((i) => i?.payer?.id === payerId);
                    if (!item || item.status === 'COMPLIANT' || item.status === 'NO_REQUIREMENT') return null;
                    const isNonCompliant = item.status === 'NON_COMPLIANT';
                    return (
                        <div
                            style={{
                                color: isNonCompliant ? '#B42318' : '#B54708',
                                background: isNonCompliant ? '#FEF3F2' : '#FFFAEB',
                                border: `1px solid ${isNonCompliant ? '#FECDCA' : '#FEC84B'}`,
                                padding: '8px 12px',
                                borderRadius: '8px',
                                marginBottom: '8px',
                                fontSize: '13px',
                            }}
                        >
                            <strong>{isNonCompliant ? 'Non-Compliant' : 'At Risk'}</strong>
                            {' — Supervision at '}
                            <strong>{item.supervisionPercent}%</strong>
                            {` (required: ${item.requirement?.minPercent}%). Consider scheduling a supervision session.`}
                        </div>
                    );
                })()}

                {showChargeRate && (
                    <div className="charge-rate-box">
                        <p className="date-time-text">Charge Rate</p>
                        {/* `null` = not fetched yet (the dispatch lands a tick before the loader), `[]` = fetched and empty. */}
                        {chargeRatesLoad?.length || !eligibleChargeRates ? (
                            <MinLoader margin={'8px 0'} color={'#347AF0'} position={'relative'} />
                        ) : chargeRateList?.length ? (
                            <div className="charge-rate-options">
                                {/* Groups are price tiers (one color each); the rates inside a tier share its color. */}
                                {eligibleChargeRates?.map((group) =>
                                    (group?.chargeRates || []).map((opt) => (
                                        <label key={opt?.chargeRateId} className="charge-rate-option">
                                            <Radio
                                                className="charge-rate-radio"
                                                checked={chargeRate?.chargeRateId === opt?.chargeRateId}
                                                onChange={() => selectChargeRate(opt)}
                                                value={opt?.chargeRateId}
                                                size="small"
                                            />
                                            <span
                                                className="charge-rate-dot"
                                                style={{ background: chargeRateColor(group?.groupIndex, eligibleChargeRates) }}
                                            />
                                            <span className="charge-rate-label">
                                                {opt?.chargeRateName}
                                                {[opt?.modifierTag, opt?.modifierTag2, opt?.modifierTag3].filter(Boolean).length
                                                    ? ` (${[opt?.modifierTag, opt?.modifierTag2, opt?.modifierTag3].filter(Boolean).join(', ')})`
                                                    : ''}
                                            </span>
                                        </label>
                                    )),
                                )}
                            </div>
                        ) : (
                            <p className="charge-rate-empty">
                                {selectedStaffId
                                    ? "This staff member doesn't qualify for any charge rate on the selected service."
                                    : 'The selected service has no charge rates.'}
                            </p>
                        )}
                    </div>
                )}

                <div style={{ alignItems: 'flex-start' }}>

                    <div className="service-inputs-wrapper" style={{alignItems: 'flex-start'}}>
                        <div className="full-width">
                            <p className="date-time-text">Staff</p>
                            <SelectStaff
                                clientStaffs={clientStaffs}
                                currentStaff={currentStaff ? currentStaff : inputs?.staff ? resetParams?.staff : null}
                                disabled={!inputs?.authorizedService}
                                handleSelectStaff={selectStaff}
                                handleRemoveStaff={() => {
                                    dispatch(appointmentActions.removeApptAddresses())
                                    const params = {
                                        ...inputs,
                                    };
                                    delete params.payCodeType;
                                    delete params.staff;
                                    delete params.location;
                                    setInputs(params);
                                    setCurrentStaff(null);
                                }}
                                typeError={error === 'staff' ? `Staff ${ErrorText.isRequired}` : ''}
                                required={!canScheduleUnassigned}
                                helperText={
                                    isUnassigned && inputs?.authorizedService
                                        ? `Staff can be assigned later. ${createMultiple ? 'These recurring appts' : 'This appt'} will remain unassigned.`
                                        : ''
                                }
                                excludeId={inputs?.supervision ? (inputs?.supervisee?.id || inputs?.supervisee || null) : null}
                            />
                            {/*{currentStaff &&*/}
                            {/*    <StaffCredential currentStaff={currentStaff} />*/}
                            {/*}*/}
                        </div>

                        <div className="full-width">
                            <p className="date-time-text" style={{height: 14}}/>
                            <SelectTypeAutocomplete
                                disabled={!inputs?.staff}
                                loadType={ServiceActionLoads.GET_ALL_PAY_CODES}
                                title={isUnassigned ? 'Staff Paycode' : 'Staff Paycode*'}
                                name={'payCodeType'}
                                handleSelect={handleChange}
                                defaultValue={inputs?.payCodeType}
                                defaultSelected={{ name: inputs?.payCodeTypeName }}
                                list={mergePayCodes(allPaycodes) || []}
                                error={error}
                                typeError={error === 'payCodeType' ? `Staff paycode ${ErrorText.isRequired}` : ''}
                                renderValue={(i) => i?.name}
                                refresh={() => getStaffPayCodes(inputs?.staff)}
                            />
                        </div>
                    </div>
                </div>


                {inputs?.supervision &&
                    <div className="flex-align-center full-width" style={{ gap: 24 }}>
                        <div className="full-width" style={{ marginBottom: 16 }}>
                            <p className="date-time-text">Supervisee</p>
                            <SelectAutocompletePagination
                                defaultStatuses={[staffStatusEnums.ACTIVE]}
                                title={'Supervisee'}
                                name={'supervisee'}
                                handleSelect={handleChange}
                                defaultValue={inputs?.supervisee?.id || inputs?.supervisee}
                                renderValue={(i) => renderStaffName(i)}
                                type={'staff'}
                                noError={true}
                                currentSelected={inputs?.supervisee}
                                filterOptions={(options) => {
                                    const staffId = inputs?.staff?.id || inputs?.staff || currentStaff?.id;
                                    return staffId ? options.filter((o) => o.id !== staffId) : options;
                                }}
                            />
                        </div>
                        <div className="full-width" />

                    </div>
                }


                <div className="service-inputs-wrapper" style={{ alignItems: 'flex-start' }}>
                    {modalDate && !createMultiple && (
                        <div className="full-width">
                            <p className="date-time-text">{createMultiple ? 'Date Range' : 'Date & Scheduled Time'}</p>
                            <DateTypeSelect
                                type={'modalInput'}
                                name={'startDate'}
                                outLabel={'Start Date*'}
                                handleGetDates={handleChange}
                                values={inputs}
                                error={error === 'startDate' && `Start date ${ErrorText.isRequired}`}
                            />
                        </div>
                    )}

                    {!modalDate && (
                        <div className="full-width">
                            <p className="date-time-text">{createMultiple ? 'Date Range' : 'Date'}</p>
                            {createMultiple ? (
                                <div className="service-inputs-box" style={{ alignItems: 'flex-start' }}>
                                    <DateTypeSelect
                                        type={'modalInput'}
                                        name={'startDate'}
                                        outLabel={'Start Date*'}
                                        handleGetDates={handleChange}
                                        values={inputs}
                                        error={error === 'startDate' && `Start date ${ErrorText.isRequired}`}
                                        max={inputs?.endDate ? moment.utc(inputs.endDate).format('YYYY-MM-DD') : ''}
                                    />
                                    <DateTypeSelect
                                        type={'modalInput'}
                                        name={'endDate'}
                                        outLabel={'End Date*'}
                                        handleGetDates={handleChange}
                                        values={inputs}
                                        error={
                                            error === 'endDate'
                                                ? `End date ${ErrorText.isRequired}`
                                                : error === ErrorText.dateError
                                                    ? ErrorText.dateBeforeAfterError
                                                    : ''
                                        }
                                        min={inputs?.startDate ? moment.utc(inputs.startDate).format('YYYY-MM-DD') : ''}
                                    />
                                </div>
                            ) : (
                                <DateTypeSelect
                                    type={'modalInput'}
                                    name={'startDate'}
                                    outLabel={'Start Date*'}
                                    handleGetDates={handleChange}
                                    values={inputs}
                                    error={error === 'startDate' && `Start date ${ErrorText.isRequired}`}
                                />
                            )}

                            {createMultiple && !modalDate && (
                                <div className="space-between">
                                    <div className="end-date-box" style={{ marginRight: 14 }}>
                                        <button
                                            onClick={() => {
                                                const newInputs = { ...inputs };
                                                newInputs.startDate = currentAuthorization?.startDate;
                                                setInputs(newInputs);
                                            }}
                                            style={{ color: inputs?.authorizedService ? '#0C66E4' : '#98A2B3' }}
                                            className="auth-end-date"
                                        >
                                            Use Auth Start Date
                                        </button>
                                    </div>
                                    <div className="end-date-box">
                                        <button
                                            onClick={() => {
                                                const newInputs = { ...inputs };
                                                newInputs.endDate = currentAuthorization?.endDate;
                                                setInputs(newInputs);
                                            }}
                                            style={{ color: inputs?.authorizedService ? '#0C66E4' : '#98A2B3' }}
                                            className="auth-end-date"
                                        >
                                            Use Auth End Date
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    <div className="full-width">
                        <p className="date-time-text">Time</p>
                        <div className="service-inputs-box" style={{ alignItems: 'flex-start' }}>
                            <TimeInput
                                label={'Start Time*'}
                                name={'startTime'}
                                onChange={handleChange}
                                typeError={error === 'startTime' ? `Start time ${ErrorText.isRequired}` : ''}
                                defaultValue={inputs?.startTime ? renderTimes(inputs.startTime) || inputs.startTime : null}
                            />
                            <div className="full-width">
                                <TimeInput
                                    label={'End Time*'}
                                    name={'endTime'}
                                    onChange={handleChange}
                                    typeError={
                                        error === 'endTime'
                                            ? `End time ${ErrorText.isRequired}`
                                            : error === ErrorText.timeError
                                                ? ErrorText.timeError
                                                : ''
                                    }
                                    defaultValue={inputs?.endTime ? renderTimes(inputs.endTime) || inputs.endTime : null}
                                />

                                {apptUnits && (
                                    <ApptUnitsSummary units={Number(apptUnits)} usage={frequencyUsage} />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="full-width flex-align-end" style={{ gap: 24, marginTop: 6 }}>
                    <div className="full-width">
                        <p className="date-time-text">Address</p>
                        <AddressTypeSelect
                            loadType={GET_APPT_ADDRESSES}
                            title={'Enter or select a location for the appt'}
                            // disabled={inputs?.telehealth ? inputs?.telehealth : !inputs?.client}
                            disabled={!inputs?.client || (!selectedStaffId && !isUnassigned)}
                            name={'location'}
                            handleSelect={handleChange}
                            value={inputs?.location}
                            list={
                                apptAddressesList(apptAddresses) || []
                            }
                            renderValue={(i) => renderLabeledAddress(i?.label, i?.name)}
                            groupBy={apptAddressGroupBy}
                            renderGroup={renderApptAddressGroup}
                            customOptions={(i) => <ApptAddressOption option={i} />}
                            noError={true}
                        />
                    </div>
                    <div className="full-width" style={{ height: 31 }}>
                        <div className="switcher-style" style={{ gap: 16 }}>
                            <p style={{ width: 'auto' }}>Is this a Telehealth appointment?</p>
                            <CustomizedSwitch
                                checked={inputs?.telehealth}
                                handleClick={() => {
                                    const newInputs = { ...inputs };
                                    // delete newInputs.location;
                                    newInputs.telehealth = !inputs.telehealth;
                                    setInputs(newInputs);
                                }}
                            />
                        </div>
                    </div>
                </div>
                {!modalDate?.id && createMultiple && (
                    <CreateMultiple
                        modalDate={modalDate}
                        state={state}
                        setState={setState}
                        inputs={{ ...inputs }}
                        setInputs={(e) => {
                            setInputs(e);
                            if (backError?.error) {
                                dispatch(httpRequestsOnErrorsActions.removeError(REQUEST_TYPE));
                            }
                            if (error === 'repeat') {
                                setError('');
                            }
                        }}
                        setOccurrence={changeOccurrence}
                        error={error}
                    />
                )}
                {checkApptRequires(modalDate) && (
                    <div className="require-box">
                        {RolePermission([PermissionList.APPT_SIGNATURE_TOGGLE_MANAGE?.code]) && (
                            <div className="switcher-style">
                                <ToolInfo text={'Require Client/Guardian signature to render this appointment.'} />
                                <p>Client/Guardian Signature Req.</p>
                                <CustomizedSwitch
                                    checked={inputs?.requireSignature}
                                    handleClick={(e) => changeRequired(e, 'requireSignature')}
                                />
                            </div>
                        )}
                        {RolePermission([PermissionList.APPT_SIGNATURE_TOGGLE_MANAGE?.code]) && (
                            <div className="switcher-style">
                                <ToolInfo text={'Require Provider signature to render this appointment.'} />
                                <p>Provider Signature Req.</p>
                                <CustomizedSwitch
                                    checked={inputs?.requireProviderSignature}
                                    handleClick={(e) => changeRequired(e, 'requireProviderSignature')}
                                />
                            </div>
                        )}
                        {RolePermission([PermissionList.APPT_NOTE_TOGGLE_MANAGE?.code]) && (
                            <div className="switcher-style">
                                <ToolInfo text={'Require notes to render this appointment.'} />
                                <p>Require Notes</p>
                                <CustomizedSwitch checked={inputs?.requireNote}
                                                  handleClick={(e) => changeRequired(e, 'requireNote')} />
                            </div>
                        )}
                    </div>
                )}

                {modalDate?.id && createMultiple && (
                    <div className="template-date-range">
                        <p className="date-time-text">Date Range</p>
                        <div className="service-inputs-box">
                            <DateTypeSelect
                                type={'modalInput'}
                                name={'betweenStartDate'}
                                outLabel={'Start Date'}
                                handleGetDates={handleChange}
                                values={inputs}
                                error={error === 'betweenStartDate' && `Start date ${ErrorText.isRequired}`}
                                noError={true}
                                min={apptBetweenDates?.startDate}
                                max={apptBetweenDates?.endDate}
                            />
                            <DateTypeSelect
                                type={'modalInput'}
                                name={'betweenEndDate'}
                                outLabel={'End Date'}
                                handleGetDates={handleChange}
                                values={inputs}
                                error={error === 'betweenEndDate' && `End date ${ErrorText.isRequired}`}
                                noError={true}
                                min={apptBetweenDates?.startDate}
                                max={apptBetweenDates?.endDate}
                            />
                        </div>

                        <div className="changes-will-apply">
                            <img src={Images.toolInfoIcon} alt={'icon'} />
                            <p>Changes will apply to all appointments between these dates.</p>
                        </div>
                    </div>
                )}
                <ApptErrors
                    staff={currentStaff}
                    inputs={inputs}
                    currentAuthorization={authorization}
                    frequencyLimit={frequencyLimit}
                    backError={backError}
                    createMultiple={createMultiple}
                    setErrorList={setErrorList}
                    errorList={errorList}
                    modalDate={modalDate}
                    occurrence={occurrence}
                    withDiagnosis
                />
                <div style={{ margin: '24px 0 0' }}>
                    {modalDate?.id && createMultiple ? (
                        <TemplateApptActions
                            apptDate={modalDate}
                            handleClose={handleOpenClose}
                            handleSave={editMultipleAppt}
                            loader={!!loader.length}
                            disabled={checkFrequencyLoad || errorList?.length !== 0 || !!loadDates?.length}
                            apptBetweenDates={apptBetweenDates}
                        />
                    ) : (
                        <CreateChancel
                            loader={!!loader.length}
                            create={
                                modalDate?.id && createMultiple
                                    ? 'Update'
                                    : modalDate?.id
                                        ? 'Save'
                                        : createMultiple
                                            ? `Add ${occurrence ? occurrence : 0} Appointments`
                                            : 'Add Appointment'
                            }
                            chancel={'Cancel'}
                            onCreate={createMultiple ? createMultipleAppt : handleCreate}
                            onClose={() => handleOpenClose()}
                            // buttonWidth="401px"
                            buttonWidth={'100%'}
                            cancelRight={'24px'}
                            disabled={checkFrequencyLoad || errorList?.length !== 0}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};
